/**
 * FormationPicker.jsx — Sélecteur d'une personne formée pour un module donné.
 * Affiche uniquement les personnes ayant suivi la formation requise (score ≥ seuil)
 * et dont la validité est < 3 ans, filtrées par site.
 *
 * Props:
 *   - module: 'permis_feu' | 'permis_levage' | 'wah' | 'espace_confine' | 'bordure_eau' | 'isolation_energies' ...
 *   - site: acronyme site (SPL, LTE, ...) pour filtrer
 *   - value: nom actuel
 *   - onChange(name, person): callback
 *   - testid
 *   - placeholder
 */
import { useEffect, useState } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';

export default function FormationPicker({ module, site, value, onChange, testid, placeholder }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!module) return;
    setLoading(true);
    api.get(`/formations/${module}`, { params: site ? { site, valid_only: true } : { valid_only: true } })
      .then(({ data }) => setPeople(data || []))
      .catch(() => setPeople([]))
      .finally(() => setLoading(false));
  }, [module, site]);

  const matched = people.find((p) => p.name === value);
  const showWarning = value && !matched;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600">
          {placeholder || 'Sélectionner une personne formée'}
        </div>
        {people.length > 0 && (
          <span className="text-[8px] px-1 py-0.5 bg-emerald-100 text-emerald-800 normal-case tracking-normal flex items-center gap-1">
            <ShieldCheck className="w-2.5 h-2.5" />
            {people.length} {site ? `formés site ${site}` : 'formés'}
          </span>
        )}
        {loading && <span className="text-[9px] text-stone-400 italic">chargement…</span>}
      </div>
      <select
        value={value || ''}
        onChange={(e) => {
          const name = e.target.value;
          const person = people.find((p) => p.name === name);
          onChange(name, person);
        }}
        data-testid={testid}
        className="w-full px-2 py-1.5 text-xs border border-stone-300 focus:border-emerald-700 outline-none bg-white"
      >
        <option value="">— Sélectionner —</option>
        {value && !matched && (
          <option value={value}>{value} (non trouvé en base)</option>
        )}
        {people.map((p) => (
          <option key={p.email} value={p.name}>
            {p.name} {p.site ? `(${p.site})` : ''} — Score {p.score}/{p.score_max} · valide jusqu'au {new Date(p.valid_until).toLocaleDateString('fr-FR')}
          </option>
        ))}
      </select>
      {showWarning && (
        <div className="mt-1 text-[10px] text-amber-700 italic inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          "{value}" n'a pas suivi la formation requise (ou validité expirée). Vérifier en base Formations.
        </div>
      )}
    </div>
  );
}
